/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import SearchService from './SearchService';
import SearchUtils from '../utils/SearchUtils';
import PreSearchService from './PreSearchService';
/**
 * This class builds a model of public search results (those that are returned by v1/search endpoint)
 * This will also handle the filtering of the results, if any is needed.
 */
export class PublicSearchManager {
	searchHostContext: HostContext;
	logger: Logger;

	constructor(searchHostContext: HostContext, logger: Logger) {
		this.searchHostContext = searchHostContext;
		this.logger = logger;
	}

	async search(
		requestBody: Record<string, any>,
		options?: { authenticated?: boolean },
	): Promise<Record<string, any>> {
		const startTime = Date.now();
		const searchService = new SearchService(this.searchHostContext);
		const res = await searchService.fetchSearchData(requestBody as searchRequestBody, {
			authenticated: options.authenticated,
		});
		const duration = Date.now() - startTime;
		this.logger.log('Public Search Manager', `Search Data Service Total: duration=${duration}`);
		SearchUtils.saveSearchTimingInSessionStorage('search', 'server', duration);

		if (!(res instanceof Error)) {
			for (const source of res.data) {
				if (source.searchResults) {
					source.category = this.getCategory(source);
					this.filterSearchResultsForCategory(source);
				} else {
					source.searchResults = {};
					source.searchResults.resultItems = [];
					source.searchResults.totalResults = 0;
					this.logger.error(
						'PublicSearchManager',
						'Missing searchResults in source data from the Search Server',
						source,
					);
				}
			}
		}

		return res;
	}

	async getPreSearchData(
		requestBody: Record<string, any>,
		options?: { authenticated?: boolean },
	): Promise<Record<string, any>> {
		const startTime = Date.now();
		const preSearchService = new PreSearchService(this.searchHostContext);
		const res = await preSearchService.fetchPreSearchData(requestBody as preSearchRequestBody, {
			authenticated: options?.authenticated,
		});
		const duration = Date.now() - startTime;
		this.logger.log('Public Search Manager', `PreSearch Data Service Total: duration=${duration}`);
		// Save the timing data to session storage
		SearchUtils.saveSearchTimingInSessionStorage('presearch', 'server', duration);

		if (!(res instanceof Error)) {
			// In case of error, create a dummy response
			for (const key in res.data) {
				const source = res.data[key];
				// check if we have a searchResults property
				if (source.searchResults) {
					// determine the category for this source
					source.category = this.getCategory(source);
				} else {
					source.searchResults = {};
					source.searchResults.resultItems = [];
					source.searchResults.totalResults = 0;
					this.logger.error(
						'PublicSearchManager',
						'Missing preSearchResults in source data from the Search Server',
						source,
					);
				}
			}
		}

		return res;
	}

	/**
	 * Returns the category of search result
	 * @param {Object} source
	 * @returns {string} category
	 */
	getCategory(source: Record<string, any>): string {
		const learnMap = new Map([
			['tutorials', 'tutorials'],
			['adobeForum', 'adobeForum'],
			['articles', 'learn'],
		]);
		let category = 'unknown';
		const id = source.id ? source.id.toLowerCase() : 'unknown';
		const firstResultItem = source?.searchResults?.resultItems?.length ? source.searchResults.resultItems[0] : {};
		switch (id) {
			case 'apps':
			case 'appcards':
				category = 'apps';
				break;

			case 'sparktemplates':
				category = 'sparkTemplates';
				break;

			case 'blended_results':
				category = 'blended_results';
				break;

			case 'stock':
				// the stock source includes two search categories - stock assets and stock templates
				// look into the resultItem array and check the value of the assetType of the first result
				firstResultItem?.assetType &&
					(category = firstResultItem?.assetType === 'template' ? 'templates' : 'stock');
				break;

			case 'learn':
				// the learn source includes three search categories - tutorials, support articles and forums
				// look into the resultItem array and check the value of the learnFilter
				if (firstResultItem?.attributes && firstResultItem?.attributes?.learnFilter) {
					const learnFilter = firstResultItem.attributes.learnFilter;
					category = learnMap.get(learnFilter) || 'unknown';
				}
				break;

			case 'plugins':
				category = 'plugins';
				break;

			default:
				category = id;
		}

		return category;
	}

	/**
	 * Filters the resultItems for a search category, as appropriate.
	 * @param {Object} searchCategory
	 */
	filterSearchResultsForCategory(searchCategory: Record<string, any>) {
		if (searchCategory.category) {
			const category = searchCategory.category?.toLowerCase() || '';
			switch (category) {
				case 'tutorials':
				case 'learn':
				case 'adobeForum':
				case 'helpcenter':
				case 'forums':
					if (searchCategory?.searchResults?.resultItems) {
						searchCategory.searchResults.resultItems = this.getFilteredLearnData(
							searchCategory.searchResults.resultItems,
							category,
						);
					}

					break;

				case 'apps':
					if (searchCategory?.searchResults?.resultItems) {
						searchCategory.searchResults.resultItems = this.getFilteredAppsData(
							searchCategory.searchResults.resultItems,
						);
					}
					break;

				case 'sparkTemplates':
				case 'blended_results':
				case 'stock':
				case 'templates':
			}
		}
	}

	/**
	 * APPS FILTERS
	 */
	getFilteredAppsData(appArray: Array<Record<string, any>>): Array<Record<string, any>> {
		let appResultsArray = appArray;

		// If the client is CCDesktop, we want to suppress the Creative Cloud app result.
		if (
			this.searchHostContext.hostInfo.surface === 'CCDesktop' &&
			this.searchHostContext.hostInfo.client === 'CCDesktop'
		) {
			appResultsArray = appArray.filter((app) => app.id !== 'KCCC');
		}

		return appResultsArray;
	}

	/**
	 * LEARN FILTERS
	 */

	/**
	 * Returns filtered data for the various learn categories.
	 * Tutorials need to be current (see isTutorialOrSupportCurrent) and have renditions and titles.
	 * Support/Forums need to be current (see isForumCurrent) and have titles.
	 * @param {Array} resultItems - search results.
	 * @param {string} category - learn category - should be one of tutorials, learn, adobeForum
	 */
	getFilteredLearnData(resultItems: Array<ResultItem>, category: string) {
		let filteredData: ResultItem[] = [];

		if (resultItems && category) {
			switch (category) {
				case 'tutorials':
					// Filter to current tutorials.
					filteredData = this.filterHelpxByDate(resultItems, category);
					// Filter to current tutorials with renditions
					filteredData = this.filterHelpxWithNoRenditions(filteredData);
					// Filter to current tutorials with renditions and titles
					filteredData = this.filterHelpxWithNoTitles(filteredData);
					break;

				case 'learn':
				case 'adobeForum':
				case 'forums':
				case 'helpcenter':
					// Filter to current support articles/forums.
					filteredData = this.filterHelpxByDate(resultItems, category);
					// Filter to current support articles/forums with titles.
					filteredData = this.filterHelpxWithNoTitles(filteredData);
					break;
			}
		}

		return filteredData;
	}

	/**
	 * Returns data filtered by date for the specified Learn source.
	 * @param {Array} resultItems - array of search result objects
	 * @param {string} category - learn category - should be one of tutorials, support, adobeForum
	 */
	filterHelpxByDate(resultItems: Array<ResultItem>, category: string): Array<ResultItem> {
		let filteredResults: ResultItem[] = [];

		switch (category?.toLowerCase() || '') {
			case 'tutorials':
			case 'learn':
			case 'helpcenter':
				filteredResults = resultItems.filter(
					(
						result, // eslint-disable-line no-confusing-arrow
					) =>
						this.isTutorialOrSupportCurrent(
							result.attributes.modificationDate || result.attributes.creationDate,
						),
				);
				break;

			case 'adobeForum':
			case 'forums':
				filteredResults = resultItems.filter(
					(
						result, // eslint-disable-line no-confusing-arrow
					) => this.isForumCurrent(result.attributes.modificationDate || result.attributes.creationDate),
				);
				break;
		}

		return filteredResults;
	}

	/**
	 * filters out tutorials with no renditions
	 * @param  {Array} data
	 * @returns {Array} filtered resultItems
	 */
	filterHelpxWithNoRenditions(resultItems: Array<ResultItem>): Array<ResultItem> {
		return resultItems.filter((result) => !!result.attributes.imageRenditionUrl);
	}

	/**
	 * filters out support with no titles
	 * @param  {Array} resultItems
	 * @returns {Array} filtered resultItems
	 */
	filterHelpxWithNoTitles(resultItems: Array<ResultItem>): Array<ResultItem> {
		return resultItems.filter((result) => !!result.attributes.name && !!result.attributes.description);
	}

	/**
	 * checks to see if the last modified date of tutorial is more recent than 1-1-2017
	 * @param  {date} number - milliseconds
	 * @returns {boolean}
	 */
	isTutorialOrSupportCurrent(date: number) {
		return date > 1483228800000; // '2017/1/1'
	}

	/**
	 * checks to see if the last modified date of forum is more recent than 1-1-2016
	 * @param  {date} number - milliseconds
	 * @returns {boolean}
	 */
	isForumCurrent(date: number) {
		return date > 1451606400000; // '2016/1/1'
	}
}
